"use client";
import { useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { PerspectiveCamera } from "three";
import { useEditorStore } from "@/stores/editorStore";
import { useSceneStore } from "@/stores/sceneStore";
import { useTimelineStore } from "@/stores/timelineStore";
import { evaluateCamera } from "@/utils/evaluateTimeline";

export function SceneCameraController() {
  const previewCameraId = useEditorStore((state) => state.previewCameraId);
  const previewCamera = useEditorStore((state) => state.previewCamera);
  const cameraObject = useSceneStore((state) => state.objects.find((object) => object.id === previewCameraId));
  const tracks = useTimelineStore((state) => state.tracks);
  const currentTime = useTimelineStore((state) => state.currentTime);
  const set = useThree((state) => state.set);
  const currentCamera = useThree((state) => state.camera);
  const size = useThree((state) => state.size);
  const initialCamera = useRef(currentCamera);
  const sceneCamera = useMemo(() => new PerspectiveCamera(), []);
  useEffect(() => { if (previewCameraId && !cameraObject) previewCamera(null); }, [cameraObject, previewCamera, previewCameraId]);
  useEffect(() => {
    const editorCamera = initialCamera.current;
    if (previewCameraId && cameraObject) set({ camera: sceneCamera });
    else set({ camera: editorCamera });
    return () => set({ camera: editorCamera });
  }, [cameraObject, previewCameraId, sceneCamera, set]);
  useFrame(() => {
    if (!cameraObject || !previewCameraId) return;
    const evaluated = evaluateCamera(tracks, previewCameraId, currentTime);
    const position = evaluated?.position ?? cameraObject.transform.position;
    const rotation = evaluated?.rotation ?? cameraObject.transform.rotation;
    sceneCamera.position.set(position.x, position.y, position.z);
    sceneCamera.rotation.set(rotation.x, rotation.y, rotation.z);
    sceneCamera.fov = cameraObject.camera?.fieldOfView ?? 50;
    sceneCamera.near = cameraObject.camera?.near ?? .1;
    sceneCamera.far = cameraObject.camera?.far ?? 300;
    sceneCamera.aspect = size.width / size.height;
    sceneCamera.updateProjectionMatrix();
  });
  return null;
}
